/**
 * 営業データ連携（新製品シミュレーション画面の「営業データ連携」タブ）
 * モックデータの計算ユーティリティ。
 */
import {
  AXIS_META,
  AxisKey,
  CURRENCY_DEFS,
  CurrencyCode,
  KPI_MOCK_JPY,
  ACTUAL_JPY,
  PLAN_JPY,
  EXPECT_CONFIRMED_JPY,
  EXPECT_UNCONFIRMED_JPY,
  ACTUAL_UPR,
  PLAN_UPR,
  EXPECT_CONFIRMED_UPR,
  EXPECT_UNCONFIRMED_UPR,
  ACTUAL_QTY,
  PLAN_QTY,
  EXPECT_CONFIRMED_QTY,
  EXPECT_UNCONFIRMED_QTY,
  ResultMetric,
  SalesDataLinkageInputState,
} from "./types";
import i18n from "../../../utils/i18n";

export const getCurrencyDef = (code: CurrencyCode) =>
  CURRENCY_DEFS.find((c) => c.code === code) ?? CURRENCY_DEFS[0];

/** JPYモック値を、指定した分析軸の係数・表示通貨に換算する */
export const convertAxisValue = (
  valueJpy: number,
  axisKey: AxisKey,
  currency: CurrencyCode
): number => {
  const factor = AXIS_META[axisKey].factor;
  const rate = getCurrencyDef(currency).rate;
  return valueJpy * factor * rate;
};

export const formatAxisAmount = (
  valueJpy: number,
  axisKey: AxisKey,
  currency: CurrencyCode
): string => {
  const def = getCurrencyDef(currency);
  const v = convertAxisValue(valueJpy, axisKey, currency);
  return `${def.symbol}${v.toLocaleString(undefined, {
    minimumFractionDigits: def.dp,
    maximumFractionDigits: def.dp,
  })}${def.unit}`;
};

/** 分析軸のKPI（表示通貨換算済み）を返す */
export const getAxisKpi = (axisKey: AxisKey, currency: CurrencyCode) => {
  const meta = AXIS_META[axisKey];
  return {
    ytdActual: formatAxisAmount(KPI_MOCK_JPY.ytdActual, axisKey, currency),
    landing: formatAxisAmount(KPI_MOCK_JPY.landing, axisKey, currency),
    prevLanding: formatAxisAmount(KPI_MOCK_JPY.prevLanding, axisKey, currency),
    confirmedExpect: formatAxisAmount(KPI_MOCK_JPY.confirmedExpect, axisKey, currency),
    achieveRate: meta.achieve,
  };
};

/** design_handoff準拠の5枚KPIカード表示値を返す */
export const getAxisKpiList = (axisKey: AxisKey, currency: CurrencyCode) => {
  const meta = AXIS_META[axisKey];
  const yoy =
    ((KPI_MOCK_JPY.landing - KPI_MOCK_JPY.prevLanding) / KPI_MOCK_JPY.prevLanding) * 100;
  const confirmedRate =
    (KPI_MOCK_JPY.confirmedExpect /
      (KPI_MOCK_JPY.confirmedExpect + KPI_MOCK_JPY.unconfirmedExpect)) *
    100;
  return {
    ytdActual: formatAxisAmount(KPI_MOCK_JPY.ytdActual, axisKey, currency),
    landing: formatAxisAmount(KPI_MOCK_JPY.landing, axisKey, currency),
    achieveRate: meta.achieve,
    yoy: Number(yoy.toFixed(1)),
    confirmedRate: Number(confirmedRate.toFixed(1)),
    prevLandingFmt: formatAxisAmount(KPI_MOCK_JPY.prevLanding, axisKey, currency),
    landingFmt: formatAxisAmount(KPI_MOCK_JPY.landing, axisKey, currency),
    confirmedExpectFmt: formatAxisAmount(KPI_MOCK_JPY.confirmedExpect, axisKey, currency),
    unconfirmedExpectFmt: formatAxisAmount(KPI_MOCK_JPY.unconfirmedExpect, axisKey, currency),
  };
};

const scaleSeries = (
  series: (number | null)[],
  axisKey: AxisKey,
  currency: CurrencyCode,
  dpOverride?: number
): (number | null)[] =>
  series.map((v) =>
    v === null
      ? null
      : Number(convertAxisValue(v, axisKey, currency).toFixed(dpOverride ?? getCurrencyDef(currency).dp))
  );

/** 数量系列を分析軸係数のみで換算する（通貨換算はしない） */
const scaleQuantitySeries = (series: (number | null)[], axisKey: AxisKey): (number | null)[] =>
  series.map((v) => (v === null ? null : Math.round(v * AXIS_META[axisKey].factor)));

/** 単価表示時の小数桁数（円・ドルとも視認性のため固定桁数とする） */
const UNIT_PRICE_DP: Record<CurrencyCode, number> = { JPY: 1, USD: 2 };

/** グラフ用の3系列（実績/計画/短期見込）を表示通貨・分析軸係数・表示指標で換算して返す */
export const getAxisChartSeries = (
  axisKey: AxisKey,
  currency: CurrencyCode,
  metric: ResultMetric = "amount"
) => {
  if (metric === "quantity") {
    return {
      actual: scaleQuantitySeries(ACTUAL_QTY, axisKey),
      plan: scaleQuantitySeries(PLAN_QTY, axisKey),
      expectConfirmed: scaleQuantitySeries(EXPECT_CONFIRMED_QTY, axisKey),
      expectUnconfirmed: scaleQuantitySeries(EXPECT_UNCONFIRMED_QTY, axisKey),
    };
  }
  if (metric === "unitPrice") {
    const dp = UNIT_PRICE_DP[currency];
    return {
      actual: scaleSeries(ACTUAL_UPR, axisKey, currency, dp),
      plan: scaleSeries(PLAN_UPR, axisKey, currency, dp),
      expectConfirmed: scaleSeries(EXPECT_CONFIRMED_UPR, axisKey, currency, dp),
      expectUnconfirmed: scaleSeries(EXPECT_UNCONFIRMED_UPR, axisKey, currency, dp),
    };
  }
  return {
    actual: scaleSeries(ACTUAL_JPY, axisKey, currency),
    plan: scaleSeries(PLAN_JPY, axisKey, currency),
    expectConfirmed: scaleSeries(EXPECT_CONFIRMED_JPY, axisKey, currency),
    expectUnconfirmed: scaleSeries(EXPECT_UNCONFIRMED_JPY, axisKey, currency),
  };
};

/** 検索結果グラフの「単位：」表示ラベルを指標・表示通貨に応じて返す */
export const getResultUnitLabel = (metric: ResultMetric, currency: CurrencyCode): string => {
  const pieces = i18n.t("common.label.pieces");
  if (metric === "quantity") return pieces;
  if (metric === "unitPrice") return `${getCurrencyDef(currency).symbol}/${pieces}`;
  return getCurrencyDef(currency).unit;
};

/** 分析軸ステップの「次へ」有効化条件: 対象外 or （検索実行済み かつ 評価入力済み） */
export const isAxisStepComplete = (
  state: SalesDataLinkageInputState,
  axisKey: AxisKey
): boolean => {
  if (!state.axisInclude[axisKey]) return true;
  return !!state.searched[axisKey] && !!state.axisEvals[axisKey];
};

/** 優位性ステップの「次へ」有効化条件: 3項目すべて評価済み */
export const isAdvantageStepComplete = (state: SalesDataLinkageInputState): boolean =>
  !!state.evals.superiority && !!state.evals.contract && !!state.evals.competition;

/** 入力確認ステップの「次へ」有効化条件: 総合評価が入力済み */
export const isConfirmStepComplete = (state: SalesDataLinkageInputState): boolean =>
  state.overallRating !== null;

export const getIncludedAxes = (axisInclude: Record<AxisKey, boolean>): AxisKey[] =>
  (Object.keys(axisInclude) as AxisKey[]).filter((k) => axisInclude[k]);

/**
 * design_handoff準拠：要因別ストレスの表示情報。
 * 符号付き%表記・バー幅（|v| / 25 を上限100%）・絶対値の大きさによる5段階配色。
 */
export const getFactorDisplay = (v: number): { pct: string; width: number; color: string } => {
  const a = Math.abs(v);
  let color: string;
  if (a <= 3) color = "#16a34a"; // 非常に良い（安定）
  else if (a <= 7) color = "#2f6bd4"; // 良い
  else if (a <= 12) color = "#d97706"; // 普通
  else if (a <= 20) color = "#ea580c"; // やや不安
  else color = "#dc2626"; // 悪い
  return { pct: `${v > 0 ? "+" : ""}${v.toFixed(1)}%`, width: Math.min(100, (a / 25) * 100), color };
};
